import { useCallback, useRef, useState } from "react";

interface PendingDelete<T> {
  item: T;
  message: string;
}

export function useUndoDelete<T>() {
  const [pending, setPending] = useState<PendingDelete<T> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const restoreActionRef = useRef<(() => void) | null>(null);
  const confirmActionRef = useRef<(() => void) | null>(null);

  const requestDelete = useCallback(
    (
      item: T,
      message: string,
      doDelete: () => void,
      doRestore: () => void,
      onConfirm?: () => void,
    ) => {
      // Confirm any previous pending delete (server sync)
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        if (confirmActionRef.current) confirmActionRef.current();
        timeoutRef.current = null;
        restoreActionRef.current = null;
        confirmActionRef.current = null;
      }

      // Delete locally only
      doDelete();
      restoreActionRef.current = doRestore;
      confirmActionRef.current = onConfirm ?? null;
      setPending({ item, message });

      // After timeout, confirm the server deletion
      timeoutRef.current = setTimeout(() => {
        if (confirmActionRef.current) confirmActionRef.current();
        setPending(null);
        restoreActionRef.current = null;
        confirmActionRef.current = null;
        timeoutRef.current = null;
      }, 4000);
    },
    [],
  );

  const undo = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    // Restore locally — server delete was never sent
    if (restoreActionRef.current) {
      restoreActionRef.current();
    }
    setPending(null);
    restoreActionRef.current = null;
    confirmActionRef.current = null;
    timeoutRef.current = null;
  }, []);

  const dismiss = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    // Confirm the server deletion on dismiss
    if (confirmActionRef.current) confirmActionRef.current();
    setPending(null);
    restoreActionRef.current = null;
    confirmActionRef.current = null;
  }, []);

  return { pending, requestDelete, undo, dismiss };
}
